// @refresh reset
import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { logAuditAction } from '@/hooks/useAuditLog';

export type AppRole = 'participant' | 'mentor' | 'admin' | 'super_admin' | 'auditor';

// Keep backward compat with old UserRole type
export type UserRole = AppRole | null;

// Orden de prioridad para elegir el rol activo por defecto cuando el usuario
// tiene varios roles y no hay preferencia guardada. 'auditor' queda último a
// propósito: es un rol aislado/de solo lectura que se combina con otros, nunca
// debe ser la identidad por defecto de alguien que también es admin/mentor/etc.
const ROLE_PRIORITY: AppRole[] = ['super_admin', 'admin', 'mentor', 'participant', 'auditor'];

interface Profile {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  company: string | null;
  position: string | null;
  area: string | null;
  organization_id: string | null;
  phone: string | null;
  linkedin_url: string | null;
  bio: string | null;
  status: string | null;
  is_public: boolean | null;
  show_projects: boolean | null;
  birth_date: string | null;
  sector: string | null;
  requires_password_reset: boolean | null;
  onboarding_completed_at: string | null;
  onboarding_checklist_dismissed_at: string | null;
  whatsapp_opt_in: boolean | null;
  whatsapp_opt_in_at: string | null;
  whatsapp_opt_out_at: string | null;
  whatsapp_opt_in_source: string | null;
}

export interface ImpersonatedUser {
  id: string;
  name: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  isLoading: boolean;
  rolesResolved: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isImpersonating: boolean;
  impersonatedAs: ImpersonatedUser | null;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [currentRole, setCurrentRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesResolved, setRolesResolved] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(() =>
    !!localStorage.getItem('_impersonation_admin_session')
  );
  const [impersonatedAs, setImpersonatedAs] = useState<ImpersonatedUser | null>(() => {
    try {
      const s = localStorage.getItem('_impersonation_target');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  // Sincroniza el header x-active-role que las policies RLS de auditor exigen
  // para aislar su acceso (además de has_role): sin esto, tener el rol
  // 'auditor' asignado junto a otro rol filtraría datos de cohortes/empresas
  // ajenas incluso navegando con la vista de ese otro rol, porque has_role()
  // no sabe qué rol tiene "activo" el usuario en el frontend.
  //
  // Se aplica SÍNCRONO en el mismo punto donde se decide el rol (no en un
  // useEffect aparte): los componentes hijos que gatean sus queries con
  // `enabled: currentRole === 'auditor'` (useCohortManagement, useUserManagement,
  // useAuditorCohortIds, etc.) reaccionan al mismo cambio de estado, y React
  // ejecuta los efectos de componentes hijos ANTES que los del padre
  // (AuthProvider) — un useEffect acá llegaría tarde para la primera carga y
  // esas queries saldrían sin el header, devolviendo 0 filas por RLS.
  const applyActiveRoleHeader = (role: UserRole) => {
    const headers = (supabase as any).rest?.headers;
    if (role) headers?.set?.('x-active-role', role);
    else headers?.delete?.('x-active-role');
  };

  // uid explícito (usado por fetchProfileAndRoles, que ya lo recibe como parámetro
  // y evita depender del closure de `user`, que puede ir un paso atrás justo durante
  // el cambio de sesión) — si no se pasa, usa el usuario actualmente logueado.
  const setCurrentRoleAndSync = (role: UserRole, uid?: string) => {
    applyActiveRoleHeader(role);
    const targetUid = uid ?? user?.id;
    // Clave por-usuario: antes era una sola clave global ('lia_current_role'), así
    // que al suplantar a otro usuario (impersonación) se heredaba el rol activo
    // guardado del ADMIN que suplanta, no el de la persona suplantada — si esa
    // persona también tenía el rol elegido, quedaba atrapada en el rol equivocado
    // (ej. viendo como admin pero con "auditor" activo, con toda la data vacía).
    if (role && targetUid) localStorage.setItem(`lia_current_role_${targetUid}`, role);
    setCurrentRole(role);
  };

  const fetchProfileAndRoles = async (userId: string) => {
    setRolesResolved(false);
    try {
      const [profileRes, rolesRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('user_roles').select('role').eq('user_id', userId),
      ]);

      if (profileRes.data) {
        setProfile(profileRes.data as unknown as Profile);
        Sentry.setUser({
          id: userId,
          email: profileRes.data.email,
          username: profileRes.data.full_name,
        });
      }

      if (rolesRes.data && rolesRes.data.length > 0) {
        const userRoles = rolesRes.data.map((r) => r.role as AppRole);
        setRoles(userRoles);
        // Rol activo: preferir el guardado para ESTE usuario; si no hay, elegir por
        // prioridad (nunca el orden crudo en que Postgres devolvió las filas, que no
        // está garantizado y podía dejar 'auditor' de default para un admin+auditor).
        const stored = localStorage.getItem(`lia_current_role_${userId}`) as AppRole | null;
        if (stored && userRoles.includes(stored)) {
          setCurrentRoleAndSync(stored, userId);
        } else {
          const defaultRole = ROLE_PRIORITY.find((r) => userRoles.includes(r)) ?? userRoles[0];
          setCurrentRoleAndSync(defaultRole, userId);
        }
      }
    } catch (error) {
      console.error('Error fetching profile/roles:', error);
    } finally {
      // Siempre liberar la espera de roles: un usuario sin rol asignado (o un
      // fallo puntual de la consulta) no debe quedarse en spinner infinito.
      setRolesResolved(true);
    }
  };

  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const isFetchingRef = useRef(false);

  const safeFetchProfileAndRoles = async (userId: string) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      await fetchProfileAndRoles(userId);
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Debounce rapid-fire TOKEN_REFRESHED events (multi-tab cascade)
        if (event === 'TOKEN_REFRESHED') {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = setTimeout(() => {
            setSession(newSession);
            setUser(newSession?.user ?? null);
          }, 1000);
          return;
        }

        // Don't set user as logged-in during password recovery — let ResetPasswordPage handle it
        if (event === 'PASSWORD_RECOVERY') {
          setIsLoading(false);
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          setTimeout(() => safeFetchProfileAndRoles(newSession.user.id), 0);
        } else {
          setProfile(null);
          setRoles([]);
          setCurrentRoleAndSync(null);
        }

        if (event === 'SIGNED_IN' && newSession?.user) {
          // Supabase emite SIGNED_IN también al volver a la pestaña, al recargar
          // y en cada pestaña abierta (BroadcastChannel), no solo en un login
          // real — auditar cada evento inflaba audit_logs (~15 "logins" por
          // usuario real). last_sign_in_at solo cambia con un inicio de sesión
          // real, y localStorage es compartido entre pestañas: se audita una
          // sola vez por cada valor distinto.
          const uid = newSession.user.id;
          const signInKey = `lia_login_audited_${uid}`;
          const signInAt = newSession.user.last_sign_in_at ?? '';
          let alreadyAudited = false;
          try {
            alreadyAudited = !!signInAt && localStorage.getItem(signInKey) === signInAt;
            if (signInAt) localStorage.setItem(signInKey, signInAt);
          } catch { /* storage bloqueado: se audita igual */ }

          const impersonationInProgress = localStorage.getItem('_impersonation_in_progress');
          if (impersonationInProgress) {
            // Session switch initiated by impersonation — skip audit log, activate banner
            localStorage.removeItem('_impersonation_in_progress');
            try {
              const target = localStorage.getItem('_impersonation_target');
              setImpersonatedAs(target ? JSON.parse(target) : null);
            } catch { /* ignore */ }
            setIsImpersonating(true);
          } else if (!alreadyAudited) {
            logAuditAction(uid, 'login', 'Inicio de sesión');
          }
        }

        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setRoles([]);
          setCurrentRoleAndSync(null);
          localStorage.removeItem('lia_current_role');
          Sentry.setUser(null);
        }

        setIsLoading(false);
      }
    );

    // Handle OAuth implicit-flow tokens returned in the URL hash.
    // The client uses flowType='pkce' so it won't auto-process #access_token=...
    // responses from Supabase's authorize endpoint. Pick them up manually.
    const handleImplicitHash = async (): Promise<boolean> => {
      try {
        const rawHash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
        if (!rawHash) return false;
        const params = new URLSearchParams(rawHash);
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        const type = params.get('type');
        // Leave recovery and error flows to their dedicated handlers.
        if (type === 'recovery') return false;
        if (params.get('error_code') || params.get('error_description')) return false;
        if (!access_token || !refresh_token) return false;
        const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
        // Clean the hash regardless to avoid loops; keep path + query.
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        if (error || !data.session) return false;
        setSession(data.session);
        setUser(data.session.user ?? null);
        if (data.session.user) safeFetchProfileAndRoles(data.session.user.id);
        setIsLoading(false);
        return true;
      } catch (e) {
        console.error('Implicit OAuth hash handling failed', e);
        return false;
      }
    };

    handleImplicitHash().then((handled) => {
      if (handled) return;
      supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
        if (existingSession?.user) {
          safeFetchProfileAndRoles(existingSession.user.id);
        }
        setIsLoading(false);
      });
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const signOut = async () => {
    const uid = user?.id;
    const orgId = profile?.organization_id;
    // Limpieza inmediata del estado local, sin depender de la red (iOS/móvil).
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setCurrentRoleAndSync(null);
    localStorage.removeItem('lia_current_role');
    Sentry.setUser(null);
    try {
      const { clearSuperAdminFilters } = await import('@/hooks/useOrgFilterStore');
      clearSuperAdminFilters();
    } catch {
      /* ignore */
    }
    const withTimeout = (p: Promise<unknown>, ms: number) =>
      Promise.race([p, new Promise((r) => setTimeout(r, ms))]).catch(() => undefined);
    if (uid) {
      await withTimeout(logAuditAction(uid, 'logout', 'Cierre de sesión', undefined, orgId), 1000);
    }
    await withTimeout(supabase.auth.signOut(), 1500);
    // Purga síncrona de tokens por si la llamada de red no terminó.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
  };

  const stopImpersonation = async () => {
    try {
      const stored = localStorage.getItem('_impersonation_admin_session');
      if (!stored) return;
      const { access_token, refresh_token } = JSON.parse(stored);
      localStorage.removeItem('_impersonation_admin_session');
      localStorage.removeItem('_impersonation_target');
      setIsImpersonating(false);
      setImpersonatedAs(null);
      await supabase.auth.setSession({ access_token, refresh_token });
    } catch (e) {
      console.error('Error stopping impersonation', e);
      localStorage.removeItem('_impersonation_admin_session');
      localStorage.removeItem('_impersonation_target');
      setIsImpersonating(false);
      setImpersonatedAs(null);
      await supabase.auth.signOut();
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfileAndRoles(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        currentRole,
        setCurrentRole: setCurrentRoleAndSync,
        isLoading,
        rolesResolved,
        signOut,
        refreshProfile,
        isImpersonating,
        impersonatedAs,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Backward-compat alias
export function useRole() {
  const { currentRole, setCurrentRole } = useAuth();
  return { currentRole, setCurrentRole, currentUserId: null, setCurrentUserId: () => {} };
}
